import { t as tr } from './i18n';
import type { PoKind, PoStatus } from './po';
import type { RentalUse } from './rental';
import type { SiteRef, SupplierRef } from './types';

/**
 * Pagina „Comenzi”: toate comenzile firmei, cu starea fiecăreia. Serverul
 * trimite faptele; starea (întârziată, de livrat) se socotește în browser, pe
 * ziua omului, ca pe panou.
 */

export interface OrderRow {
  id: string;
  rfqId: string;
  number: string;
  kind: PoKind;
  status: PoStatus;
  supplier: SupplierRef;
  site: SiteRef;
  rfqTitle: string;
  category: string;
  /** valoarea, lei fără TVA (la chirie livrată, pe zilele reale) */
  valueLei: number;
  /** ISO 8601: emisă (sau trimisă la aprobare) */
  issuedAt: string;
  /** 'AAAA-LL-ZZ': data dorită de pe comandă; null = nescrisă */
  deliveryDate: string | null;
  /** măcar o livrare pe aviz */
  delivered: boolean;
  /** ISO 8601: plecată la furnizor (din Procumi sau „Am trimis-o”); null = netrimisă */
  sentAt?: string | null;
  /** livrări trecute de pe șantier, încă neconfirmate de Achiziții */
  toConfirm?: number;
  completedAt: string | null;
  rental: RentalUse | null;
  createdBy: string | null;
  /** autorul, pentru avatar: `avatarAt` = poza lui, dacă și-a pus una */
  author?: { id: string; name: string; avatarAt: string | null } | null;
  /** la cele care așteaptă: pasul de acum, din câți, și cine trebuie să decidă */
  approval: { step: number; steps: number; approver: string | null } | null;
  /** motivul, la cele anulate sau respinse */
  reason: string;
}

export type OrderState =
  | 'pending'
  | 'unsent'
  | 'late'
  | 'waiting'
  | 'onsite'
  | 'delivered'
  | 'done'
  | 'rejected'
  | 'canceled';

export function orderState(o: OrderRow, today: string): OrderState {
  if (o.status === 'pending') return 'pending';
  if (o.status === 'rejected') return 'rejected';
  if (o.status === 'canceled') return 'canceled';
  if (o.completedAt) return 'done';
  // emisă, dar furnizorul n-o are încă: nu e „de livrat” și nici „întârziată”
  if (!o.delivered && o.sentAt === null) return 'unsent';
  if (!o.delivered) return o.deliveryDate && o.deliveryDate < today ? 'late' : 'waiting';
  return o.rental && !o.rental.to ? 'onsite' : 'delivered';
}

/** Eticheta și culoarea pastilei (clasele `.pill` din aplicație). */
export const ORDER_STATE: Record<OrderState, { label: string; pill: string }> = {
  pending: {
    get label() {
      return tr('așteaptă aprobare');
    },
    pill: 'wait',
  },
  unsent: {
    get label() {
      return tr('netrimisă');
    },
    pill: 'wait',
  },
  late: {
    get label() {
      return tr('livrare întârziată');
    },
    pill: 'late',
  },
  waiting: {
    get label() {
      return tr('de livrat');
    },
    pill: 'todo',
  },
  onsite: {
    get label() {
      return tr('pe șantier');
    },
    pill: 'act',
  },
  delivered: {
    get label() {
      return tr('livrată pe aviz, de închis');
    },
    pill: 'got',
  },
  done: {
    get label() {
      return tr('livrată integral');
    },
    pill: 'off',
  },
  rejected: {
    get label() {
      return tr('respinsă');
    },
    pill: 'off',
  },
  canceled: {
    get label() {
      return tr('anulată');
    },
    pill: 'off',
  },
};

/** Contoarele de sus, în ordinea în care cer ceva de la tine. */
export const ORDER_GROUPS = [
  {
    id: 'all',
    get label() {
      return tr('Toate');
    },
    states: null,
  },
  {
    id: 'pending',
    get label() {
      return tr('Așteaptă aprobare');
    },
    states: ['pending'],
  },
  {
    id: 'open',
    get label() {
      return tr('De livrat');
    },
    states: ['unsent', 'waiting', 'late'],
  },
  {
    id: 'late',
    get label() {
      return tr('Întârziate');
    },
    states: ['late'],
  },
  {
    id: 'toClose',
    get label() {
      return tr('De închis');
    },
    states: ['delivered', 'onsite'],
  },
  {
    id: 'done',
    get label() {
      return tr('Livrate integral');
    },
    states: ['done'],
  },
  {
    id: 'void',
    get label() {
      return tr('Respinse și anulate');
    },
    states: ['rejected', 'canceled'],
  },
] as const satisfies readonly { id: string; label: string; states: readonly OrderState[] | null }[];

export type OrderGroup = (typeof ORDER_GROUPS)[number]['id'];

export function inGroup(o: OrderRow, group: OrderGroup, today: string): boolean {
  const g = ORDER_GROUPS.find((x) => x.id === group)!;
  return g.states === null || (g.states as readonly OrderState[]).includes(orderState(o, today));
}
